import { cva, type VariantProps } from 'class-variance-authority';
import { AlertTriangle, CheckCircle2, Info, XCircle } from 'lucide-react';
import type { HTMLAttributes, ReactNode } from 'react';
import { cn } from '../../lib/cn.js';

const alertVariants = cva('flex gap-3 rounded-card p-4 text-sm', {
  variants: {
    variant: {
      default: 'bg-surface-sunken text-text',
      brand: 'bg-accent-soft text-accent-soft-text',
      success: 'bg-success/10 text-success',
      warning: 'bg-warning/10 text-warning',
      danger: 'bg-danger/10 text-danger',
      info: 'bg-info/10 text-info',
    },
  },
  defaultVariants: { variant: 'default' },
});

const ICONS = {
  default: Info,
  brand: Info,
  success: CheckCircle2,
  warning: AlertTriangle,
  danger: XCircle,
  info: Info,
} as const;

export interface AlertProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'title'>,
    VariantProps<typeof alertVariants> {
  title?: ReactNode;
  /** Pass `false` to drop the leading icon. */
  icon?: boolean;
}

export function Alert({ className, variant, title, icon = true, children, ...props }: AlertProps) {
  const Icon = ICONS[variant ?? 'default'];

  return (
    <div role="alert" className={cn(alertVariants({ variant }), className)} {...props}>
      {icon ? <Icon className="mt-0.5 size-4 shrink-0" aria-hidden="true" /> : null}
      <div className="min-w-0 flex-1">
        {title ? <p className="font-semibold">{title}</p> : null}
        {children ? <div className={cn(title && 'mt-1', 'opacity-90')}>{children}</div> : null}
      </div>
    </div>
  );
}
